import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import HFHeader from "@/components/HFHeader";
import HFFooter from "@/components/HFFooter";
import ShareCard from "@/components/ShareCard";
import Video360Viewer from "@/components/Video360Viewer";
import Photo360Viewer from "@/components/Photo360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import RefugiumSkyboxSection from "@/components/RefugiumSkyboxSection";
import { Container, HFButton } from "@/components/ui-hf";
import { Lock, MapPin, Calendar, Home as HomeIcon, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

type Media = {
    id: string;
    original_filename: string;
    mime: string;
    media_type: string;
    poster_filename?: string | null;
    poster_url?: string | null;
    stored_url?: string | null;
    hls_url?: string | null;
    hls_ready?: boolean;
    title: string;
    description?: string;
    alt?: string;
    width?: number;
    height?: number;
    pair_id?: string | null;
    pair_role?: "before" | "after" | null;
};
type ProjectPair = {
    id: string;
    before_media_id: string | null;
    after_media_id: string | null;
    title: string | null;
    sort_order: number;
    before_url?: string | null;
    after_url?: string | null;
};
type Project = {
    id?: string;
    slug: string;
    title: string;
    subtitle?: string;
    description_short?: string;
    description_long?: string;
    object_type?: string;
    location?: string;
    capture_date?: string | null;
    cover_media_id?: string | null;
    cover_url?: string | null;
    cover_poster_url?: string | null;
    visibility: "public" | "private";
    password_protected?: boolean;
    status?: string;
    media?: Media[];
    pairs?: ProjectPair[];
};

type FetchState =
    | { kind: "loading" }
    | { kind: "not_found" }
    | { kind: "private_wall"; project: Project }
    | { kind: "ready"; project: Project };

const TYPE_LABEL: Record<string, string> = {
    photo: "FOTO",
    photo_360: "360° FOTO",
    video: "VIDEO",
    video_360: "360° VIDEO",
    fisheye: "FISHEYE",
    tiny_planet: "TINY PLANET",
    before: "VORHER",
    after: "NACHHER",
};

const mediaSrc = (id: string) => `${BACKEND_URL}/api/media/${id}`;
const posterSrc = (id: string) => `${BACKEND_URL}/api/media/${id}/poster`;
// Poster für einen Media-Eintrag: R2 poster_url zuerst, dann Backend-Redirect,
// erst dann Media-URL als Fallback. Für Videos ohne Poster ⇒ null (Placeholder).
const posterForMedia = (m: Media): string | null => {
    if (m.poster_url) return m.poster_url;
    if (m.poster_filename) return posterSrc(m.id);
    return null;
};
// Poster für Video360Viewer-Layer-1-Overlay (HTML `<img>`). Direkte R2-URL
// bevorzugen — schneller als Backend-Proxy, keine CORS-Restriktion für `<img>`.
const posterForWebGL = (m: Media): string | null => {
    if (m.poster_url) return m.poster_url;
    if (m.poster_filename) return posterSrc(m.id);
    return null;
};
const thumbForMedia = (m: Media) => {
    if (m.mime.startsWith("video/")) {
        return posterForMedia(m) || posterSrc(m.id);
    }
    return m.stored_url || mediaSrc(m.id);
};

async function fetchProject(
    slug: string,
    password?: string,
): Promise<{ status: number; data: Project | null }> {
    const headers: HeadersInit = {};
    if (password) headers["X-Project-Password"] = password;
    const res = await fetch(`${BACKEND_URL}/api/projects/${slug}`, {
        credentials: "include",
        headers,
    });
    if (res.status === 200) {
        const data = (await res.json()) as Project;
        return { status: 200, data };
    }
    return { status: res.status, data: null };
}

export default function ProjectPage() {
    const { slug = "" } = useParams<{ slug: string }>();
    const [state, setState] = useState<FetchState>({ kind: "loading" });
    const [pwError, setPwError] = useState<string>("");
    const [pwBusy, setPwBusy] = useState(false);
    const [lightbox, setLightbox] = useState<Media | null>(null);

    const load = useCallback(
        async (password?: string) => {
            const { status, data } = await fetchProject(slug, password);
            if (status === 401) {
                return { ok: false, reason: "wrong_pw" as const };
            }
            if (status === 429) {
                return { ok: false, reason: "rate_limited" as const };
            }
            if (!data) {
                setState({ kind: "not_found" });
                return { ok: false, reason: "not_found" as const };
            }
            const isPrivate = data.visibility === "private";
            const hasFullPayload = "description_short" in data || "media" in data;
            if (isPrivate && !hasFullPayload) {
                setState({ kind: "private_wall", project: data });
                return { ok: true, reason: "wall" as const };
            }
            setState({ kind: "ready", project: data });
            return { ok: true, reason: "ok" as const };
        },
        [slug],
    );

    useEffect(() => {
        void load();
    }, [load]);

    const submitPassword = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const pw = String(fd.get("password") || "");
        if (!pw) return;
        setPwBusy(true);
        setPwError("");
        const r = await load(pw);
        setPwBusy(false);
        if (!r.ok && r.reason === "wrong_pw") {
            setPwError("Das Passwort ist leider nicht korrekt.");
        } else if (!r.ok && r.reason === "rate_limited") {
            setPwError("Zu viele Versuche. Bitte in 10 Minuten erneut probieren.");
        }
    };

    if (state.kind === "loading") {
        return (
            <div className="min-h-screen">
                <HFHeader />
                <Container className="pt-24 pb-24">
                    <span className="hf-eyebrow text-hf-muted">Wird geladen…</span>
                </Container>
                <HFFooter />
            </div>
        );
    }

    if (state.kind === "not_found") {
        return (
            <div className="min-h-screen">
                <Helmet>
                    <title>Projekt nicht gefunden · HypeFly</title>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <HFHeader />
                <Container className="pt-24 pb-24 max-w-2xl text-center">
                    <div className="hf-eyebrow">404</div>
                    <h1 className="mt-3 font-display text-4xl">Projekt nicht gefunden.</h1>
                    <p className="mt-4 text-hf-muted">
                        Es gibt kein öffentliches Projekt unter dieser Adresse. Vielleicht wurde es zurückgezogen.
                    </p>
                    <Link to="/" className="hf-btn hf-btn-primary mt-8 inline-flex">
                        Zur Startseite
                    </Link>
                </Container>
                <HFFooter />
            </div>
        );
    }

    if (state.kind === "private_wall") {
        return (
            <div className="min-h-screen">
                <Helmet>
                    <title>Privates Projekt · HypeFly</title>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <HFHeader />
                <Container className="pt-24 pb-24 max-w-md">
                    <div
                        className="p-8 rounded-sm"
                        style={{
                            background: "hsl(var(--hf-bg))",
                            border: "1px solid hsl(var(--hf-line))",
                        }}
                        data-testid="project-password-wall"
                    >
                        <span
                            className="inline-flex items-center justify-center w-12 h-12 rounded-full"
                            style={{
                                background: "hsl(var(--hf-gold-soft))",
                                color: "hsl(var(--hf-ink))",
                            }}
                        >
                            <Lock size={20} />
                        </span>
                        <div className="hf-eyebrow mt-5">Privates Projekt</div>
                        <h1 className="mt-3 font-display text-3xl leading-tight">
                            {state.project.title || "Zugangskennwort erforderlich"}
                        </h1>
                        <p className="mt-3 text-sm text-hf-muted">
                            Dieses Projekt ist privat. Bitte geben Sie das Zugangskennwort ein, das Sie von HypeFly erhalten haben.
                        </p>
                        <form onSubmit={submitPassword} className="mt-6 space-y-3">
                            <input
                                type="password"
                                name="password"
                                required
                                autoFocus
                                placeholder="Zugangskennwort"
                                className="w-full px-4 py-3 border rounded-sm bg-white text-sm"
                                style={{ borderColor: "hsl(var(--hf-line))" }}
                                data-testid="project-password-input"
                            />
                            {pwError && (
                                <div
                                    className="text-sm px-3 py-2 rounded-sm"
                                    style={{
                                        background: "hsl(var(--hf-danger) / 0.12)",
                                        color: "hsl(var(--hf-danger))",
                                    }}
                                    data-testid="project-password-error"
                                >
                                    {pwError}
                                </div>
                            )}
                            <HFButton
                                type="submit"
                                variant="primary"
                                disabled={pwBusy}
                                data-testid="project-password-submit"
                                trailing={<span aria-hidden>→</span>}
                            >
                                {pwBusy ? "Bitte warten…" : "Öffnen"}
                            </HFButton>
                        </form>
                    </div>
                </Container>
                <HFFooter />
            </div>
        );
    }

    // ---------- Ready ----------
    const p = state.project;
    const media = p.media || [];
    // Cover-Hierarchie (P0-Fix):
    //  1. Backend-aufgelöstes `cover_url` (funktioniert auch wenn das
    //     gewählte Cover-Medium NICHT in media_ids steht)
    //  2. media.find(id === cover_media_id) für Alt-Kompatibilität
    //  3. Erstes zugeordnetes Medium (Poster bei Videos / stored_url bei Fotos)
    //  4. Kein Bild → dunkler Ink-Hintergrund als Fallback (kein leerer Cream-Bereich)
    const coverFromMedia: Media | null = p.cover_media_id
        ? media.find((m) => m.id === p.cover_media_id) || null
        : media[0] || null;
    const heroBg: string | null =
        p.cover_url || (coverFromMedia ? thumbForMedia(coverFromMedia) : null);
    const cover = coverFromMedia;
    const video360 = media.find((m) => m.media_type === "video_360");
    const otherMedia = media.filter((m) => m.id !== (video360?.id || ""));

    // Vorher/Nachher-Paare aus dem neuen `pairs`-Array des Projekts.
    // Backend liefert bereits before_url/after_url (nur wenn beide Bilder valide).
    const projectPairs: ProjectPair[] = (p.pairs || [])
        .filter((pr: ProjectPair) => !!(pr.before_url && pr.after_url))
        .sort((a: ProjectPair, b: ProjectPair) => (a.sort_order || 0) - (b.sort_order || 0));
    const isPublic = p.visibility === "public";
    const publicUrl = `https://www.hypefly.de/projekt/${p.slug}`;
    const captureDate = p.capture_date
        ? new Date(p.capture_date).toLocaleDateString("de-DE", {
              timeZone: "Europe/Berlin",
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : "";

    return (
        <div className="min-h-screen">
            <Helmet>
                {isPublic ? (
                    <>
                        <title>{`${p.title} · HypeFly`}</title>
                        <meta name="description" content={p.description_short || p.subtitle || "360°-Immobiliendokumentation"} />
                        <meta property="og:title" content={`${p.title} · HypeFly`} />
                        <meta property="og:description" content={p.description_short || "360°-Immobiliendokumentation"} />
                        <meta property="og:type" content="article" />
                        <meta property="og:url" content={publicUrl} />
                        {heroBg && <meta property="og:image" content={heroBg} />}
                        <meta name="twitter:card" content="summary_large_image" />
                    </>
                ) : (
                    <>
                        <title>Privates Projekt · HypeFly</title>
                        <meta name="robots" content="noindex, nofollow" />
                    </>
                )}
            </Helmet>
            <HFHeader />
            <main id="main-content" data-testid="project-main">
                {/* HERO */}
                <section
                    id="project-hero"
                    className="relative overflow-hidden"
                    style={{
                        minHeight: "min(78vh, 800px)",
                        background: heroBg ? "hsl(var(--hf-ink))" : "hsl(var(--hf-ink))",
                    }}
                    data-testid="project-hero"
                >
                    {heroBg ? (
                        <>
                            <img
                                src={heroBg}
                                alt={cover?.alt || p.title}
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ opacity: 0.68 }}
                                data-testid="project-hero-bg-image"
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, hsl(var(--hf-ink) / 0.20) 0%, hsl(var(--hf-ink) / 0.42) 55%, hsl(var(--hf-ink) / 0.92) 100%)",
                                }}
                            />
                        </>
                    ) : (
                        /* Kein Cover → dezenter Gold-Verlauf auf Ink-Dunkel (kein leerer Cream) */
                        <div
                            className="absolute inset-0"
                            style={{
                                background:
                                    "radial-gradient(ellipse at 25% 35%, hsl(var(--hf-gold) / 0.10) 0%, hsl(var(--hf-ink)) 60%)",
                            }}
                            data-testid="project-hero-solid-fallback"
                        />
                    )}
                    <Container className="relative pt-24 md:pt-36 pb-16 md:pb-24">
                        <div className="max-w-3xl">
                            <div
                                className="hf-eyebrow inline-flex items-center gap-3"
                                style={{ color: "hsl(var(--hf-gold-soft))" }}
                            >
                                <span className="inline-block w-8 h-px" style={{ background: "hsl(var(--hf-gold-soft))" }} />
                                HypeFly · Projekt
                            </div>
                            <h1
                                className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05]"
                                style={{ color: "hsl(var(--hf-bg))" }}
                                data-testid="project-title"
                            >
                                {p.title}
                            </h1>
                            {p.subtitle && (
                                <p
                                    className="mt-3 text-lg md:text-xl"
                                    style={{ color: "hsl(var(--hf-bg) / 0.85)" }}
                                >
                                    {p.subtitle}
                                </p>
                            )}
                            <div
                                className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
                                style={{ color: "hsl(var(--hf-bg) / 0.85)" }}
                            >
                                {p.object_type && (
                                    <span className="inline-flex items-center gap-2">
                                        <HomeIcon size={14} /> {p.object_type}
                                    </span>
                                )}
                                {p.location && (
                                    <span className="inline-flex items-center gap-2">
                                        <MapPin size={14} /> {p.location}
                                    </span>
                                )}
                                {captureDate && (
                                    <span className="inline-flex items-center gap-2">
                                        <Calendar size={14} /> {captureDate}
                                    </span>
                                )}
                            </div>
                            {p.description_short && (
                                <p className="mt-6 max-w-2xl text-hf-bg/90 text-base md:text-lg leading-relaxed">
                                    {p.description_short}
                                </p>
                            )}
                        </div>
                    </Container>
                </section>

                {/* 360° HIGHLIGHT */}
                {video360 && (
                    <section
                        id="project-video360"
                        className="py-16 md:py-24"
                        style={{ background: "hsl(var(--hf-surface) / 0.6)" }}
                        data-testid="project-video360-section"
                    >
                        <Container>
                            <div className="mb-8">
                                <div className="hf-eyebrow">Signature</div>
                                <h2 className="font-display text-3xl md:text-4xl mt-2">
                                    360°-Rundgang
                                </h2>
                                <p className="mt-2 text-hf-muted max-w-2xl">
                                    Frei drehbar während des Abspielens — mit Maus, Finger oder Gyroskop.
                                </p>
                            </div>
                            <ViewerErrorBoundary
                                testId="project-video360-fallback"
                                fallbackTitle="360°-Video kann nicht gestartet werden"
                            >
                                <Video360Viewer
                                    src={mediaSrc(video360.id)}
                                    hlsSrc={video360.hls_ready ? `${mediaSrc(video360.id)}/hls/index.m3u8` : undefined}
                                    poster={posterForWebGL(video360) || undefined}
                                    testIdPrefix="project-video360"
                                    autoTour={true}
                                    mediaId={video360.id}
                                    mediaTitle={video360.title || p.title}
                                    projectId={p.id}
                                />
                            </ViewerErrorBoundary>
                        </Container>
                    </section>
                )}

                {/* DESCRIPTION + SHARE */}
                <section className="py-16 md:py-24" data-testid="project-description">
                    <Container className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
                        <div className="lg:col-span-2 max-w-prose">
                            <div className="hf-eyebrow">Dokumentation</div>
                            <h2 className="font-display text-3xl md:text-4xl mt-2">
                                Zum Objekt
                            </h2>
                            {p.description_long ? (
                                <div className="mt-5 space-y-4 text-base md:text-lg text-hf-muted leading-relaxed whitespace-pre-line">
                                    {p.description_long}
                                </div>
                            ) : (
                                <p className="mt-5 text-hf-muted">
                                    Weitere Details folgen in Kürze.
                                </p>
                            )}
                        </div>
                        {isPublic && (
                            <aside className="lg:col-span-1">
                                <ShareCard
                                    url={publicUrl}
                                    testIdPrefix="project-share"
                                />
                            </aside>
                        )}
                    </Container>
                </section>

                {/* VORHER / NACHHER Sektion */}
                {projectPairs.length > 0 && (
                    <section
                        id="project-beforeafter"
                        className="py-16 md:py-24"
                        data-testid="project-before-after"
                    >
                        <Container>
                            <div className="mb-10">
                                <div className="hf-eyebrow">Vergleich</div>
                                <h2 className="font-display text-3xl md:text-4xl mt-2">Vorher · Nachher</h2>
                                <p className="mt-2 text-hf-muted text-sm md:text-base max-w-2xl">
                                    Ziehen Sie den Regler, um den direkten Vergleich zu sehen.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                                {projectPairs.map((pr) => (
                                    <figure key={pr.id} data-testid={`project-pair-${pr.id}`}>
                                        <BeforeAfterSlider
                                            beforeUrl={pr.before_url as string}
                                            afterUrl={pr.after_url as string}
                                        />
                                        {pr.title && (
                                            <figcaption className="mt-2 text-sm font-display">
                                                {pr.title}
                                            </figcaption>
                                        )}
                                    </figure>
                                ))}
                            </div>
                        </Container>
                    </section>
                )}

                {/* Refugium-Sonder-Sektion: 360°-Rundgang durch die Räume.
                    Rendert nur wenn der Projekt-Slug „refugium" ist — sonst versteckt.
                    Filtert intern selbst nach photo_360-Titeln „Refugium – <Raum> Rohbau/Fertig".
                */}
                {p.slug === "refugium" && <RefugiumSkyboxSection media={media} />}

                {/* GALLERY */}
                {otherMedia.length > 0 && (
                    <section
                        id="project-gallery"
                        className="py-16 md:py-24"
                        style={{ background: "hsl(var(--hf-surface) / 0.6)" }}
                        data-testid="project-gallery"
                    >
                        <Container>
                            <div className="mb-10">
                                <div className="hf-eyebrow">Galerie</div>
                                <h2 className="font-display text-3xl md:text-4xl mt-2">
                                    {otherMedia.length} Aufnahmen
                                </h2>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {otherMedia.map((m) => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        onClick={() => setLightbox(m)}
                                        className="text-left group relative aspect-[4/3] overflow-hidden rounded-sm"
                                        style={{
                                            background: "hsl(var(--hf-surface))",
                                            border: "1px solid hsl(var(--hf-line))",
                                        }}
                                        data-testid={`project-media-${m.id}`}
                                    >
                                        <img
                                            src={thumbForMedia(m)}
                                            alt={m.alt || m.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                                        />
                                        <span
                                            className="absolute top-2 left-2 text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm"
                                            style={{
                                                background: "hsl(var(--hf-ink) / 0.78)",
                                                color: "hsl(var(--hf-gold-soft))",
                                            }}
                                        >
                                            {TYPE_LABEL[m.media_type] || m.media_type}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </Container>
                    </section>
                )}

                {/* CTA */}
                <section
                    id="project-cta"
                    className="py-20 md:py-32"
                    style={{
                        background: "hsl(var(--hf-ink))",
                        color: "hsl(var(--hf-bg))",
                    }}
                    data-testid="project-cta"
                >
                    <Container className="max-w-3xl text-center">
                        <div
                            className="hf-eyebrow"
                            style={{ color: "hsl(var(--hf-gold-soft))" }}
                        >
                            Ihr Anwesen
                        </div>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl">
                            Auch Ihre Immobilie so bewahren?
                        </h2>
                        <p className="mt-4 md:text-lg text-hf-bg/80">
                            Wir kommen zu Ihnen, dokumentieren jedes Detail in 360° — persönlich, diskret, ohne Show.
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <Link
                                to={`/?ref=${encodeURIComponent(p.slug)}#kontakt`}
                                className="hf-btn hf-btn-primary"
                                data-testid="project-cta-contact"
                            >
                                Unverbindlich anfragen{" "}
                                <span aria-hidden>→</span>
                            </Link>
                            <Link
                                to="/"
                                className="hf-btn hf-btn-ghost"
                                style={{ color: "hsl(var(--hf-bg))", borderColor: "hsl(var(--hf-bg) / 0.3)" }}
                                data-testid="project-cta-home"
                            >
                                Mehr über HypeFly
                            </Link>
                        </div>
                    </Container>
                </section>
            </main>

            {/* Lightbox */}
            {lightbox && (
                <MediaLightbox media={lightbox} onClose={() => setLightbox(null)} />
            )}

            <HFFooter />
        </div>
    );
}

function MediaLightbox({
    media,
    onClose,
}: {
    media: Media;
    onClose: () => void;
}) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6"
            style={{ background: "rgba(14,23,38,0.92)" }}
            role="dialog"
            aria-modal="true"
            data-testid="project-lightbox"
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Schließen"
                className="absolute top-5 right-5 w-11 h-11 inline-flex items-center justify-center rounded-full"
                style={{
                    background: "hsl(var(--hf-bg) / 0.15)",
                    color: "hsl(var(--hf-bg))",
                    border: "1px solid hsl(var(--hf-bg) / 0.3)",
                }}
                data-testid="project-lightbox-close"
            >
                <X size={18} />
            </button>
            <div
                className="w-full max-w-5xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-3">
                    <span
                        className="text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded-sm"
                        style={{
                            background: "hsl(var(--hf-gold))",
                            color: "hsl(var(--hf-ink))",
                        }}
                    >
                        {TYPE_LABEL[media.media_type] || media.media_type}
                    </span>
                    <div className="mt-2 font-display text-2xl text-hf-bg">
                        {media.title}
                    </div>
                </div>
                {media.media_type === "video_360" ? (
                    <ViewerErrorBoundary
                        testId="lightbox-video360-fallback"
                        fallbackTitle="360°-Video nicht möglich"
                    >
                        <Video360Viewer
                            src={mediaSrc(media.id)}
                            hlsSrc={media.hls_ready ? `${mediaSrc(media.id)}/hls/index.m3u8` : undefined}
                            poster={posterForWebGL(media) || undefined}
                            testIdPrefix="lightbox-video360"
                            autoPlay={true}
                            autoTour={true}
                            mediaId={media.id}
                            mediaTitle={media.title}
                        />
                    </ViewerErrorBoundary>
                ) : media.media_type === "photo_360" ? (
                    <ViewerErrorBoundary
                        testId="lightbox-photo360-fallback"
                        fallbackTitle="360°-Foto nicht möglich"
                    >
                        {/*
                          Photo Sphere Viewer benutzt intern `fetch` mit
                          CORS-Mode. Der Preview-Overlay wrapped `window.fetch`
                          und fügt Credentials hinzu, was R2-CORS verletzt.
                          Der `/proxy`-Endpoint streamt Same-Origin durch das
                          Backend — keine CORS-Prüfung nötig, keine Redirects.
                        */}
                        <Photo360Viewer
                            src={`${mediaSrc(media.id)}/proxy`}
                            testIdPrefix="lightbox-photo360"
                            mediaId={media.id}
                            mediaTitle={media.title}
                        />
                    </ViewerErrorBoundary>
                ) : media.mime.startsWith("video/") ? (
                    <video
                        src={mediaSrc(media.id)}
                        poster={posterForMedia(media) || undefined}
                        controls
                        playsInline
                        className="w-full rounded-sm bg-black"
                        style={{ maxHeight: "80vh" }}
                    />
                ) : (
                    <img
                        src={mediaSrc(media.id)}
                        alt={media.alt || media.title}
                        className="w-full h-auto max-h-[80vh] object-contain rounded-sm bg-black"
                    />
                )}
                {media.description && (
                    <p className="mt-3 text-sm text-hf-bg/80 max-w-3xl">
                        {media.description}
                    </p>
                )}
            </div>
        </div>
    );
}
